import { type UserFilters, useUsers } from "@/hooks/use-users";
import type { Env } from "@/lib/types";
import { Plus, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { UserPanel } from "./user-panel";
import { UsersTable } from "./users-table";

interface UsersPageProps {
  env?: Env;
}

export function UsersPage({ env }: UsersPageProps = {}) {
  const [filters, setFilters] = useState<UserFilters>({ page: 1, page_size: 25 });
  const [search, setSearch] = useState("");
  const [panelId, setPanelId] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState(false);

  const { data, isLoading } = useUsers(env !== undefined ? { ...filters, env } : filters);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    const timeout = setTimeout(() => {
      setFilters((f) => {
        const next: UserFilters = { ...f, page: 1 };
        if (value) next.q = value;
        // biome-ignore lint/performance/noDelete: exactOptionalPropertyTypes requires delete
        else delete next.q;
        return next;
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Filter bar + create button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search
              size={12}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--iris-fg-5)",
              }}
            />
            <input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="iris-input"
              style={{ paddingLeft: 28, width: 240 }}
            />
          </div>
          <select
            className="iris-input"
            style={{ cursor: "pointer" }}
            value={filters.status || ""}
            onChange={(e) =>
              setFilters((f) => {
                const next: UserFilters = { ...f, page: 1 };
                if (e.target.value) next.status = e.target.value;
                // biome-ignore lint/performance/noDelete: exactOptionalPropertyTypes requires delete
                else delete next.status;
                return next;
              })
            }
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            className="iris-input"
            style={{ cursor: "pointer" }}
            value={filters.role || ""}
            onChange={(e) =>
              setFilters((f) => {
                const next: UserFilters = { ...f, page: 1 };
                if (e.target.value) next.role = e.target.value;
                // biome-ignore lint/performance/noDelete: exactOptionalPropertyTypes requires delete
                else delete next.role;
                return next;
              })
            }
          >
            <option value="">All Types</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button
          type="button"
          className="iris-btn iris-btn--primary"
          onClick={() => setCreateMode(true)}
        >
          <Plus size={14} /> Create User
        </button>
      </div>

      {/* Table card */}
      <div className="iris-card" style={{ padding: 0, overflow: "hidden" }}>
        {isLoading ? (
          <div
            style={{
              padding: "24px 18px",
              textAlign: "center",
              color: "var(--iris-fg-4)",
              fontSize: 13,
            }}
          >
            Loading...
          </div>
        ) : (
          <>
            <UsersTable
              users={data?.users ?? []}
              filters={filters}
              onFiltersChange={setFilters}
              onRowClick={(id) => setPanelId(id)}
            />

            {/* Pagination */}
            {data && data.total > (data.page_size ?? 25) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderTop: "1px solid var(--iris-line-1)",
                  fontSize: 12,
                  color: "var(--iris-fg-4)",
                }}
              >
                <span>
                  Showing {(data.page - 1) * data.page_size + 1}&ndash;
                  {Math.min(data.page * data.page_size, data.total)} of {data.total}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="iris-btn"
                    disabled={data.page <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="iris-btn"
                    disabled={data.page * data.page_size >= data.total}
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <UserPanel
        userId={panelId}
        open={panelId !== null || createMode}
        onClose={() => {
          setPanelId(null);
          setCreateMode(false);
        }}
        createMode={createMode}
        env={env}
      />
    </div>
  );
}
